// src/utils/sections.ts
import { AuthController } from "@/control/auth";

export function formatDate(timestamp: string | Date): string {
    if (!timestamp) {
      return "";
    }
  
    const date = new Date(timestamp);
  
    if (isNaN(date.getTime())) {
      console.error("Invalid timestamp provided to formatDateOnly:", timestamp);
      return "";
    }
  
    const dateOptions: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };
  
    return date.toLocaleDateString("es-ES", dateOptions); // Ej: "02/07/2025"
  }

/**
 * Formats a given timestamp into a localized date and time string.
 *
 * @param timestamp - The timestamp to format (e.g., ISO string, Date object).
 * @returns A formatted string like "DD/MM/YYYY HH:MM" or an empty string if the timestamp is invalid.
 */
export function formatDateTime(timestamp: string | Date): string {
    if (!timestamp) {
      return "";
    }

    const date = new Date(timestamp);
  
    if (isNaN(date.getTime())) {
      console.error("Invalid timestamp provided to formatDateTime:", timestamp);
      return "";
    }
  
    const dateOptions: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric" };
    const timeOptions: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hour12: false };
  
    const formattedDate = date.toLocaleDateString("es-ES", dateOptions);
    const formattedTime = date.toLocaleTimeString("es-ES", timeOptions);
  
    return `${formattedDate} ${formattedTime}`;
  }

export interface Section {
    uri: string;
    icon: string;
    name: string;
}

export function formatDateTimeTimestamp(timestamp: string | Date): string {
    if (!timestamp) {
      return "";
    }
  
    const date = new Date(timestamp);
  
    if (isNaN(date.getTime())) {
      console.error("Invalid timestamp provided to formatDateTime:", timestamp);
      return "";
    }
  
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  }

export function getSections(): Section[] {
    const sections: Section[] = [];

    if (AuthController.Status !== "UNAUTHORIZED") {

         if (["admin"].includes(AuthController.Role)) {
            sections.unshift({
                uri: "questionnaires-statistics",
                icon: "chart-line",
                name: "Questionnaires statistics",
            });
        }

        sections.unshift({
            uri: "satisfaction-questionnaires",
            icon: "thumbs-up",
            name: "Satisfaction Questionnaires",
        });

        sections.unshift({
            uri: "assistant",
            icon: "user-cog",
            name: "Assistant",
        });

        if (AuthController.Role === "patient") {
            sections.unshift({
                uri: "account-charts",
                icon: "chart-line",
                name: "Statistics",
            });
            sections.unshift({
                uri: "assigned-workers",
                icon: "user",
                name: "Assigned workers",
            });
            sections.unshift({
                uri: "answer-forms",
                icon: "clipboard-list",
                name: "Answer forms",
            });
            sections.unshift({
                uri: "my-medical-data",
                icon: "notes-medical",
                name: "My medical data",
            });
        }

        if (["doctor", "carer"].includes(AuthController.Role)) {
            sections.unshift({
                uri: "assigned-patients",
                icon: "hospital-user",
                name: "Assigned patients",
            });
        }

        if (["admin", "doctor"].includes(AuthController.Role)) {
            sections.unshift({
                uri: "forms",
                icon: "list",
                name: "Forms",
            });
        }

        if (AuthController.Role === "admin") {
            sections.unshift({
                uri: "users",
                icon: "user",
                name: "Users",
            });
        }
    }

    return sections;
}

/**
 * Convert an array buffer to a Base64 string
 * @param buffer Array buffer to convert
 * @returns {string} Base64 string
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[ i ]);
    }
    return window.btoa(binary);
}

/**
 * Download an uploaded image as a Base64 string
 * @param filename File name
 * @param onSuccessCallback Callback with a single string argument, called on success
 * @param onErrorCallback Callback with a single string argument, called on error
 */
export function downloadRemoteImage(
    filename: string, onSuccessCallback: CallableFunction, onErrorCallback?: CallableFunction
): void {
    const backendHost = import.meta.env.DEV
        ? import.meta.env.VITE___DEV_TEST_HOST
        : import.meta.env.VITE__API_SERVER_HOST;
    fetch(`${backendHost}/public/${filename}`, {
        method: "GET",
        headers: { "x-session-id": AuthController.Session },
    })
        .then(response => {
            if (response.status === 200) {
                response.arrayBuffer()
                    .then(buf => onSuccessCallback(arrayBufferToBase64(buf)))
            } else if (onErrorCallback !== undefined && onErrorCallback !== null) {
                onErrorCallback(response.text);
            }
        })
}
